import { ApiError, request } from '../api/client';
import { imageFormatsWording, looksLikeImage } from '../photos/imageFile';
import { resizePhoto } from '../photos/resize';

/**
 * Снимки уже заведённой карточки: добавить, назначить главным, удалить.
 *
 * <p><b>Зачем это отдельно от приёмки.</b> Там снимок делают телефоном
 * в момент, когда деталь заводят, и он уходит через офлайн-очередь: в ангаре
 * связи нет. Здесь всё наоборот — владелец за компьютером доснимает то,
 * что приехало переносом без картинок или было принято второпях. Очередь
 * тут не нужна и вредна: она откладывает работу, которую человек ждёт
 * сейчас, и прячет отказ хранилища за «отправим позже». Та же причина,
 * по которой продажа не идёт через очередь.
 *
 * <p><b>Три шага, как и в приёмке.</b> Ссылка, загрузка прямо в хранилище,
 * подтверждение. Снимок весит сотни килобайт, и гонять его через приложение
 * значит занимать его потоки на минуты — при десятке фотографий подряд это
 * заметно.
 */
export interface Upload {
  photoId: number;
  key: string;
  uploadUrl: string;
}

export async function uploadPhoto(partId: number, file: File): Promise<void> {
  // Отказ не-картинке стоит ЗДЕСЬ, а не в уменьшении, и это то же решение,
  // что назвал владелец: различать обязан слой, который знает, откуда файл.
  // Сюда файл приходит с диска, выбранный в диалоге, — и это может быть
  // что угодно, вплоть до PDF, переименованного в .jpg. Снимок с камеры идёт
  // другим путём (PhotoPicker → офлайн-очередь), и там «не удалось уменьшить»
  // по-прежнему означает «отправить как есть»: потерять фотографию приёмщика
  // хуже, чем занять лишнее место.
  if (!(await looksLikeImage(file))) {
    throw new ApiError(
      'permanent',
      0,
      `«${file.name}» — это не картинка. Приложить можно фотографию: ${imageFormatsWording()}`,
    );
  }

  // Уменьшение до отправки: снимок с телефона весит пять мегабайт, а в
  // карточке и в прайсе площадки от них не остаётся ничего, кроме времени
  // загрузки.
  const resized = await resizePhoto(file);

  const upload = await request<Upload>(`/api/parts/${partId}/photos/upload-url`, {
    method: 'POST',
    // Ключ клиента: повтор после обрыва вернёт ту же запись и новую ссылку,
    // а не заведёт второй снимок.
    body: { contentType: resized.contentType, requestId: crypto.randomUUID() },
  });

  const put = await fetch(upload.uploadUrl, {
    method: 'PUT',
    // Content-Type входит в подпись: другой здесь — отказ хранилища.
    headers: { 'Content-Type': resized.contentType },
    body: resized.blob,
  }).catch(() => null);

  if (put === null || !put.ok) {
    throw new ApiError('transient', put?.status ?? 0, 'Снимок не загрузился в хранилище');
  }

  await request(`/api/parts/${partId}/photos/${upload.photoId}/confirm`, {
    method: 'POST',
    body: { width: resized.width, height: resized.height },
  });
}

/** Главный снимок — тот, что уезжает на площадку и стоит в списке склада. */
export function makeMainPhoto(partId: number, photoId: number): Promise<void> {
  return request<void>(`/api/parts/${partId}/photos/${photoId}/main`, { method: 'POST' });
}

export function deletePhoto(partId: number, photoId: number): Promise<void> {
  return request<void>(`/api/parts/${partId}/photos/${photoId}`, { method: 'DELETE' });
}

/**
 * Адрес архива со всеми снимками позиции.
 *
 * <p>Ссылкой, а не запросом через `request`: браузер обязан **сохранить**
 * файл, а не отдать его коду — имя файла сервер называет в заголовке ответа,
 * и повторять его здесь значило бы завести второе место, где оно считается.
 * Так же сделаны выгрузки витрины и колёс.
 *
 * <p>Собирает архив сервер: ссылки на снимки подписанные и короткоживущие,
 * а девять параллельных скачиваний с телефона по мобильной связи кончаются
 * отказом на половине.
 */
export function photoArchiveUrl(partId: number): string {
  return `/api/parts/${partId}/photos/archive`;
}
